import type { ComponentProps, ReactNode } from 'react'
import { fbt } from 'fbtee'
import { CalendarHeart, Music, Users } from 'lucide-react'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { memberCountLabel } from '../../lib/commonStrings'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import type { Group } from '../../types'
import { ThumbStack } from '../../components/profile/ThumbStack'

function Tile({ thumbs, label, ...button }: { thumbs: ReactNode; label: ReactNode } & ComponentProps<'button'>) {
  return (
    <button
      type="button"
      className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-2 rounded-lg p-2 text-left hover:bg-muted"
      {...button}
    >
      {thumbs}
      <span className="text-xs leading-tight text-muted-foreground">{label}</span>
    </button>
  )
}

/**
 * What the viewer has in common with the person whose profile this is —
 * plans, artists, groups — as counts with photos. Nothing when they share nothing.
 */
export function TogetherCard({
  sharedEventImages,
  sharedEventCount,
  sharedArtistImages,
  sharedArtistCount,
  sharedGroups,
  onShowEvents,
  onShowArtists,
}: {
  sharedEventImages: (string | undefined)[]
  sharedEventCount: number
  sharedArtistImages: (string | undefined)[]
  sharedArtistCount: number
  sharedGroups: Group[]
  onShowEvents: () => void
  onShowArtists: () => void
}) {
  const navigate = useTransitionNavigate()
  if (sharedEventCount === 0 && sharedArtistCount === 0 && sharedGroups.length === 0) return null

  const crewsTile = (
    <Tile
      thumbs={<ThumbStack srcs={sharedGroups.map(g => g.imageUrl)} icon={Users} />}
      label={
        <fbt desc="Tile under the In common heading on someone's profile: how many groups you're both in">
          <fbt:plural count={sharedGroups.length} showCount="yes" many="groups">
            group
          </fbt:plural>
        </fbt>
      }
      onClick={sharedGroups.length === 1 ? () => navigate(`/groups/${sharedGroups[0].id}`, { state: { entryPoint: 'profile' } }) : undefined}
    />
  )

  return (
    <section className="rounded-xl border border-border bg-card p-2" data-testid="profile-together">
      <p className="px-2 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {fbt('In common', 'Heading of the card on someone’s profile summarizing what you share with them')}
      </p>
      <div className="flex gap-1">
        {sharedEventCount > 0 && (
          <Tile
            thumbs={<ThumbStack srcs={sharedEventImages} icon={CalendarHeart} />}
            label={
              <fbt desc="Tile under the In common heading on someone's profile: how many upcoming events are in both your plans">
                <fbt:plural count={sharedEventCount} showCount="yes" many="plans">
                  plan
                </fbt:plural>
              </fbt>
            }
            onClick={onShowEvents}
          />
        )}
        {sharedArtistCount > 0 && (
          <Tile
            thumbs={<ThumbStack srcs={sharedArtistImages} icon={Music} round />}
            label={
              <fbt desc="Tile under the In common heading on someone's profile: how many artists you both follow">
                <fbt:plural count={sharedArtistCount} showCount="yes" many="artists">
                  artist
                </fbt:plural>
              </fbt>
            }
            onClick={onShowArtists}
          />
        )}
        {sharedGroups.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{crewsTile}</DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="min-w-60 p-1.5">
              {sharedGroups.map(group => (
                <DropdownMenuItem
                  key={group.id}
                  onSelect={() => navigate(`/groups/${group.id}`, { state: { entryPoint: 'profile' } })}
                  className="gap-3 py-1.5"
                >
                  <ThumbStack srcs={[group.imageUrl]} icon={Users} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{group.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {memberCountLabel(group.memberIds.length)}
                    </span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : sharedGroups.length === 1 && crewsTile}
      </div>
    </section>
  )
}
